import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { ChevronDown, Receipt, Search, Wallet } from "lucide-react";
import clsx from "clsx";
import {
  downloadAccountingExport,
  downloadMonthlyAccountingExport,
  type AccountingExportFormat,
} from "@/lib/accountingExport";
import { api } from "@/lib/apiClient";
import { useCurrentUser } from "@/lib/auth";
import { formatIsoDateShort } from "@/lib/bookingDates";
import { formatCurrency } from "@/lib/money";
import { usePageHeader } from "@/layout/usePageHeader";
import { Button } from "@/components/ui/Button";
import { ClickableTableRow } from "@/components/ui/ClickableTableRow";
import { InvoiceTypeBadge } from "@/components/invoices/InvoiceTypeBadge";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Table } from "@/components/ui/Table";
import { InvoiceDetailModal } from "@/pages/invoices/InvoiceDetailModal";
import type { InvoiceListItem, InvoiceListResponse } from "@/types/camping";

const PAGE_SIZE = 50;

function todayLocalDate(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function firstDayOfMonth(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

type InvoiceFilter = "all" | "draft" | "open" | "overdue" | "dunnable" | "paid" | "cancelled";

const FILTER_OPTIONS: { value: InvoiceFilter; label: string }[] = [
  { value: "all", label: "Alle" },
  { value: "draft", label: "Entwürfe" },
  { value: "open", label: "Offene Posten" },
  { value: "overdue", label: "Überfällig" },
  { value: "dunnable", label: "Mahnbedürftig" },
  { value: "paid", label: "Bezahlt" },
  { value: "cancelled", label: "Storniert" },
];

const MONTH_LABELS = Array.from({ length: 12 }, (_, index) =>
  new Intl.DateTimeFormat("de-DE", { month: "long" }).format(new Date(2026, index, 1)),
);

function parseFilterParam(value: string | null): InvoiceFilter {
  if (
    value === "draft" ||
    value === "open" ||
    value === "overdue" ||
    value === "dunnable" ||
    value === "paid" ||
    value === "cancelled"
  ) {
    return value;
  }
  return "all";
}

export function InvoicesPage() {
  const { setPageMeta } = usePageHeader();
  const { user } = useCurrentUser();
  const groups = useMemo(() => (user?.groups as string[] | undefined) ?? [], [user]);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const initialFilter = parseFilterParam(searchParams.get("filter"));

  const [items, setItems] = useState<InvoiceListItem[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [openTotal, setOpenTotal] = useState<string>("0");
  const [currency, setCurrency] = useState("EUR");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<InvoiceFilter>(initialFilter);
  const [searchInput, setSearchInput] = useState("");
  const [bookingInput, setBookingInput] = useState("");
  const [search, setSearch] = useState("");
  const [bookingNumber, setBookingNumber] = useState("");
  const [selected, setSelected] = useState<InvoiceListItem | null>(null);
  const [exportOpen, setExportOpen] = useState(false);

  const [exportFromDate, setExportFromDate] = useState(firstDayOfMonth());
  const [exportToDate, setExportToDate] = useState(todayLocalDate());
  const [exportFormat, setExportFormat] = useState<AccountingExportFormat>("datev");
  const [exportMonth, setExportMonth] = useState(() => new Date().getMonth() + 1);
  const [exportYear, setExportYear] = useState(() => new Date().getFullYear());
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const params = new URLSearchParams({
      limit: String(PAGE_SIZE),
      offset: String(offset),
      filter,
    });
    if (search.trim()) params.set("q", search.trim());
    if (bookingNumber.trim()) params.set("booking_number", bookingNumber.trim());

    api
      .get<InvoiceListResponse>(`/api/v1/invoices?${params}`)
      .then((res) => {
        setItems(res.items);
        setTotal(res.total);
        setOpenTotal(res.open_receivables_total);
        setCurrency(res.currency);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, [offset, filter, search, bookingNumber]);

  useEffect(() => {
    setPageMeta({
      title: "Rechnungen",
      subtitle: "Offene Posten, Belege und Export",
    });
    return () => setPageMeta(null);
  }, [setPageMeta]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    if (filter === "all") {
      params.delete("filter");
    } else {
      params.set("filter", filter);
    }
    const next = params.toString();
    const current = searchParams.toString();
    if (next !== current) {
      navigate(next ? `/invoices?${next}` : "/invoices", { replace: true });
    }
  }, [filter, navigate, searchParams]);

  function onFilterSubmit(e: React.FormEvent) {
    e.preventDefault();
    const nextSearch = searchInput.trim();
    const nextBooking = bookingInput.trim();
    const sameQuery = nextSearch === search && nextBooking === bookingNumber;
    setSearch(nextSearch);
    setBookingNumber(nextBooking);
    if (offset !== 0) {
      setOffset(0);
      return;
    }
    if (sameQuery) load();
  }

  function hasOpenAmount(inv: InvoiceListItem): boolean {
    if (inv.status === "draft" || inv.status === "cancelled" || inv.open_amount == null) return false;
    const amount = Number(inv.open_amount);
    return Number.isFinite(amount) && amount > 0.0001;
  }

  async function onExportAccounting(e: React.FormEvent) {
    e.preventDefault();
    setExportError(null);
    setExporting(true);
    try {
      await downloadAccountingExport(exportFromDate, exportToDate, exportFormat);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  }

  async function onExportMonthlyAccounting() {
    setExportError(null);
    setExporting(true);
    try {
      await downloadMonthlyAccountingExport(exportYear, exportMonth, exportFormat);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : "Export fehlgeschlagen.");
    } finally {
      setExporting(false);
    }
  }

  const displayDate = (inv: InvoiceListItem) =>
    inv.issue_date ? formatIsoDateShort(inv.issue_date) : "—";

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageIndex = Math.floor(offset / PAGE_SIZE) + 1;
  const resultLabel = loading
    ? "Lade Rechnungen…"
    : total === 0
      ? "Keine Rechnungen für diese Ansicht."
      : `${total.toLocaleString("de-DE")} ${total === 1 ? "Rechnung" : "Rechnungen"}${
          total > PAGE_SIZE ? ` · Seite ${pageIndex} von ${pageCount}` : ""
        }`;

  return (
    <div className="space-y-4">
      <section className="eh-card flex flex-col gap-4 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent-soft text-accent">
            <Receipt size={20} aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-medium uppercase tracking-wide text-label-secondary">
              Offene Posten
            </p>
            <p className="mt-0.5 text-3xl font-semibold tabular-nums tracking-tight text-label">
              {formatCurrency(openTotal, currency)}
            </p>
            <p className="mt-1 max-w-xl text-xs leading-relaxed text-label-tertiary">
              Überfällig: Fälligkeit vor heute und noch offener Betrag. Mahnbedürftig: überfällig,
              Mahnstufe unter 3.
            </p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="secondary"
            aria-expanded={exportOpen}
            aria-controls="invoice-export"
            leftIcon={
              <ChevronDown
                size={16}
                aria-hidden
                className={clsx("transition-transform", exportOpen && "rotate-180")}
              />
            }
            onClick={() => setExportOpen((open) => !open)}
          >
            Export
          </Button>
          <Link
            to="/cashbook"
            className="inline-flex h-10 items-center justify-center gap-2 rounded-lg bg-card px-4 text-sm font-medium text-label ring-1 ring-inset ring-separator-opaque transition hover:bg-fill-tertiary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          >
            <Wallet size={16} aria-hidden />
            Kassenjournal
          </Link>
        </div>
      </section>

      {exportOpen ? (
        <section id="invoice-export" className="eh-card px-4 py-4 sm:px-5">
          <h2 className="text-sm font-semibold text-label">Buchhaltung exportieren</h2>
          <p className="mt-1 max-w-3xl text-xs text-label-secondary">
            DATEV-Buchungsstapel für Lexware (Erlöse, Zahlungen, Stornos mit Gegenkonto und
            BU-Schlüssel) oder ein einfaches CSV.
          </p>
          <label className="mt-4 block max-w-sm text-sm">
            <span className="mb-1 block text-label-secondary">Format</span>
            <select
              className="eh-input w-full"
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as AccountingExportFormat)}
            >
              <option value="datev">DATEV Buchungsstapel (Lexware)</option>
              <option value="csv">Einfaches CSV</option>
            </select>
          </label>
          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <form
              className="rounded-xl bg-fill-tertiary/60 p-3"
              onSubmit={(e) => {
                e.preventDefault();
                void onExportMonthlyAccounting();
              }}
            >
              <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                Monat
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-[1fr_7rem]">
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Monat</span>
                  <select
                    className="eh-input w-full"
                    value={exportMonth}
                    onChange={(e) => setExportMonth(Number(e.target.value))}
                  >
                    {MONTH_LABELS.map((label, index) => (
                      <option key={label} value={index + 1}>
                        {label}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Jahr</span>
                  <input
                    type="number"
                    className="eh-input w-full"
                    min={2000}
                    max={2100}
                    value={exportYear}
                    onChange={(e) => setExportYear(Number(e.target.value))}
                  />
                </label>
              </div>
              <Button
                type="submit"
                variant="secondary"
                className="mt-3"
                disabled={exporting}
              >
                {exporting ? "Export…" : "Monat exportieren"}
              </Button>
            </form>
            <form className="rounded-xl bg-fill-tertiary/60 p-3" onSubmit={onExportAccounting}>
              <p className="text-xs font-semibold uppercase tracking-wide text-label-secondary">
                Zeitraum
              </p>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Von</span>
                  <input
                    type="date"
                    className="eh-input w-full"
                    value={exportFromDate}
                    onChange={(e) => setExportFromDate(e.target.value)}
                    required
                  />
                </label>
                <label className="block text-sm">
                  <span className="mb-1 block text-label-secondary">Bis</span>
                  <input
                    type="date"
                    className="eh-input w-full"
                    value={exportToDate}
                    onChange={(e) => setExportToDate(e.target.value)}
                    required
                  />
                </label>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  disabled={exporting}
                  onClick={() => {
                    const today = todayLocalDate();
                    setExportFromDate(today);
                    setExportToDate(today);
                  }}
                >
                  Heute
                </Button>
                <Button type="submit" variant="secondary" disabled={exporting}>
                  {exporting ? "Export…" : "Zeitraum exportieren"}
                </Button>
              </div>
            </form>
          </div>
        </section>
      ) : null}
      {exportError ? <p className="text-sm text-status-red">{exportError}</p> : null}
      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <Table
        wide
        emptyMessage="Keine Rechnungen für die gewählten Filter."
        isEmpty={!loading && items.length === 0}
        toolbar={
          <div className="flex flex-col gap-3 border-b border-separator px-4 py-3 sm:px-5">
            <div className="overflow-x-auto">
              <div
                className="eh-segmented eh-segmented--md min-w-max"
                role="tablist"
                aria-label="Rechnungsstatus"
              >
                {FILTER_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={filter === opt.value}
                    className={clsx("eh-segmented-item", filter === opt.value && "is-active")}
                    onClick={() => {
                      setFilter(opt.value);
                      setOffset(0);
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
            <form
              onSubmit={onFilterSubmit}
              className="flex flex-col gap-2 sm:flex-row sm:items-center"
            >
              <div className="relative min-w-0 flex-1">
                <Search
                  size={16}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-label-tertiary"
                  aria-hidden
                />
                <input
                  type="search"
                  className="eh-input w-full pl-9"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Gast oder Rechnungsnummer"
                  aria-label="Gast oder Rechnungsnummer"
                  autoComplete="off"
                />
              </div>
              <input
                type="search"
                className="eh-input w-full sm:w-52"
                value={bookingInput}
                onChange={(e) => setBookingInput(e.target.value)}
                placeholder="Buchungsnummer"
                aria-label="Buchungsnummer"
                autoComplete="off"
              />
              <Button type="submit" className="w-full sm:w-auto" disabled={loading}>
                Suchen
              </Button>
            </form>
            <p className="text-xs text-label-tertiary">{resultLabel}</p>
          </div>
        }
      >
        <thead>
          <tr>
            <th>Rechnung</th>
            <th>Gast</th>
            <th>Datum</th>
            <th className="text-right">Betrag</th>
            <th>Status</th>
            <th>Fälligkeit</th>
          </tr>
        </thead>
        <tbody>
          {items.map((inv) => (
            <ClickableTableRow
              key={inv.id}
              ariaLabel={`Rechnung ${inv.invoice_number} öffnen`}
              onActivate={() => setSelected(inv)}
            >
              <td data-label="Rechnung">
                <div className="font-mono text-xs">{inv.invoice_number}</div>
                <div className="mt-1">
                  <InvoiceTypeBadge invoiceType={inv.invoice_type} />
                </div>
              </td>
              <td className="font-medium" data-label="Gast">
                {inv.guest_name}
                {inv.booking_number ? (
                  <span className="mt-0.5 block font-mono text-xs font-normal text-label-secondary">
                    {inv.booking_number}
                  </span>
                ) : null}
              </td>
              <td className="whitespace-nowrap" data-label="Datum">
                {displayDate(inv)}
              </td>
              <td className="text-right" data-label="Betrag">
                <div className="font-medium tabular-nums">
                  {formatCurrency(inv.total_amount, inv.currency)}
                </div>
                {hasOpenAmount(inv) ? (
                  <div
                    className={clsx(
                      "mt-0.5 text-xs font-medium tabular-nums",
                      inv.is_overdue ? "text-status-red" : "text-status-orange",
                    )}
                  >
                    offen {formatCurrency(inv.open_amount, inv.currency)}
                  </div>
                ) : null}
              </td>
              <td data-label="Status">
                <div className="flex flex-wrap items-center gap-1.5">
                  <StatusBadge status={inv.status} kind="invoice" />
                  {inv.is_overdue ? (
                    <span className="rounded-full bg-status-red/15 px-2 py-0.5 text-[0.6875rem] font-medium text-status-red">
                      Überfällig
                    </span>
                  ) : null}
                  {inv.is_dunnable ? (
                    <span className="rounded-full bg-status-orange/15 px-2 py-0.5 text-[0.6875rem] font-medium text-status-orange">
                      Mahnfähig
                    </span>
                  ) : null}
                </div>
              </td>
              <td data-label="Fälligkeit">
                <div className={inv.is_overdue ? "font-medium text-status-red" : "text-label-secondary"}>
                  {inv.due_date ? formatIsoDateShort(inv.due_date) : "—"}
                </div>
                {inv.dunning_level != null && inv.dunning_level > 0 ? (
                  <div className="mt-0.5 text-xs text-label-tertiary">Stufe {inv.dunning_level}</div>
                ) : null}
              </td>
            </ClickableTableRow>
          ))}
        </tbody>
      </Table>

      {total > PAGE_SIZE ? (
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button
            type="button"
            variant="secondary"
            disabled={offset === 0 || loading}
            onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
          >
            Zurück
          </Button>
          <span className="px-1 text-sm tabular-nums text-label-secondary">
            {pageIndex} / {pageCount}
          </span>
          <Button
            type="button"
            variant="secondary"
            disabled={offset + PAGE_SIZE >= total || loading}
            onClick={() => setOffset((o) => o + PAGE_SIZE)}
          >
            Weiter
          </Button>
        </div>
      ) : null}

      <InvoiceDetailModal
        invoice={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        userGroups={groups}
        onChanged={load}
      />
    </div>
  );
}
